import React, { useEffect, useState, useContext, useRef } from "react";
import axios from "axios";
import {
  MapPin,
} from "lucide-react";

import { useConfig } from "../../utils/ConfigContext.jsx";
import { createAxiosInstance } from "../../utils/AxiosInstance.jsx";
import FilterContext from '../../utils/FilterContext';
import { set } from "date-fns";
import { get } from "jquery";
import { Link, useLocation } from "react-router-dom";

export const App = () => {

  const { API_BASE_URL } = useConfig(); // ✅ from context
  const AxiosInstance = createAxiosInstance(API_BASE_URL);

  const location = useLocation();
  const currentPath = location.pathname;

  // AbortController refs for managing API requests
  const abortControllersRef = useRef({});
  const isMountedRef = useRef(true);

  const [ownStates, setOwnStates] = useState([]);        // Own centers
  const [partnershipStates, setPartnershipStates] = useState([]);  // Partnership centers
  const [ownWidgets, setOwnWidgets] = useState({});      // Own center widget data
  const [partnershipWidgets, setPartnershipWidgets] = useState({});  // Partnership widget data
  const [ownLoading, setOwnLoading] = useState({});      // Own centers loader
  const [partnershipLoading, setPartnershipLoading] = useState({});  // Partnership loader

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() - 1);
  const previousDay = currentDate.toISOString().split("T")[0];


  // Access filter data and setter function from context
  const { filters, setFilters } = useContext(FilterContext);
  const filtersRef = useRef(filters);
  const [rawStart, rawEnd] = filters?.dateRange?.split(" - ") ?? [];

  const startDate = rawStart?.trim() || previousDay;
  const endDate = rawEnd?.trim() || previousDay;

  const [showCenterModal, setShowCenterModal] = useState(false);
  const [allCentersData, setAllCentersData] = useState([]); // To hold all centers data for modal
  const [OwnCentersData, setOwnCentersData] = useState([]);
  const [PartnershipCentersData, setPartnershipCentersData] = useState([]);

  const [leadModel, setLeadModel] = useState(false);
  const [appointmentModel, setAppointmentModel] = useState(false);
  const [patientModel, setPatientModel] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [dataCategory, setDataCategory] = useState("");
  const [patientModalHeader, setPatientModalHeader] = useState("");
  const [isFullScreenModal, setIsFullScreenModal] = useState(false);
  const [isPatientModalFromTotal, setIsPatientModalFromTotal] = useState(false);
  const [centerOrGroupName, setCenterOrGroupName] = useState("");
  const [selectedLeadStatus, setSelectedLeadStatus] = useState("");

  const stateOrderMap = {
    "Uttarakhand": 0,
    "West Bengal": 1,
    "Maharashtra": 2,
    "Karnataka": 3,
    "Tamil Nadu": 4,
    "Madhya Pradesh": 5,
    "New Delhi": 6,
  };

  const LeadStatusMap = {
    0: "New",
    1: "Scheduled",
    2: "Follow Up",
    3: "Converted",
    4: "Closed",
    5: "Transferred",
  };

  const TYPE_MAP = {
    1: "SDT",
    2: "SNB",
  };

  const PackageStatusMap = {
    0: 'Not Started',
    1: 'Activated',
    2: 'Completed',
    3: "Cancelled",
    4: "Cancelling"
  }

  const TABLE_CONFIG = {
    package: {
      label: "Package Details",
      key: "package_details",
      typeLabel: "Package Type",
      dateLabel: "Booking Date",
      getType: (item) => TYPE_MAP[item.package_type],
      getDate: (item) => formatedDate(item.tppm_date),
      getLastTreatmentDate: (item) => item.last_treatment_date ? formatedDate(item.last_treatment_date) : '',
      getStatus: (item) => PackageStatusMap[item.status],
      getSessions: (item) => item.sessions,
    },
    treatment: {
      label: "Treatment Details",
      key: "treatment_details",
      typeLabel: "Treatment Type",
      dateLabel: "Treatment Date",
      getType: (item) => TYPE_MAP[item.treatment_type],
      getDate: (item) => formatedDate(item.ttm_date),
      getLastTreatmentDate: (item) => item.last_treatment_date ?? '',
      getStatus: (item) => PackageStatusMap[item.package_status],
      getSessions: (item) => item.total_sessions,
    },
  };

  const mode =
    dataCategory === "Patients Treated" ? "treatment" : "package";

  const config = TABLE_CONFIG[mode];

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (pm_id) => {
    setExpandedRows(prev => ({
      ...prev,
      [pm_id]: !prev[pm_id]
    }));
  };

  //format date DD-MM-YYYY
  const formatedDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Fetch all states first
  useEffect(() => {
    isMountedRef.current = true;

    if (!filters) return; // Guard against null filters

    if (!filtersRef.current || filtersRef.current !== filters) {
      filtersRef.current = filters;
      fetchStates(1).then(() => {
        return fetchStates(2);  // Partnership centers
      });
    }

    // Cleanup function when component unmounts or filters change
    return () => {
      isMountedRef.current = false;
      abortAllRequests();
    };

  }, [filters]);

  // Abort all pending requests
  const abortAllRequests = () => {
    Object.values(abortControllersRef.current).forEach(controller => {
      if (controller) controller.abort();
    });
    abortControllersRef.current = {};
  };

  const fetchStates = async (category) => {
    try {
      const controller = new AbortController();
      const key = `fetchStates_${category}`;
      abortControllersRef.current[key] = controller;

      const res = await AxiosInstance.get("/centers/" + category, {
        signal: controller.signal
      });
      const centers = res.data.data || [];
      //grouping states
      const stateGroups = centers.reduce((groups, center) => {
        const state = center.hm_state;
        if (!groups.find((g) => g.hm_state === state)) {
          groups.push({ hm_state: state });
        }
        return groups;
      }, []);


      const sortedStates = (stateGroups || []).sort(
        (a, b) =>
          (stateOrderMap[a.hm_state] ?? 999) -
          (stateOrderMap[b.hm_state] ?? 999)
      );


      let finalStates = sortedStates;

      // Remove New Delhi only for Own centers
      if (category === 1) {
        finalStates = sortedStates.filter(
          (st) => st.hm_state !== "New Delhi"
        );
        if (isMountedRef.current) setOwnStates(finalStates);
        if (isMountedRef.current) setOwnCentersData(centers);  // Store all own centers data
      }

      if (category === 2) {
        if (isMountedRef.current) setPartnershipStates(finalStates);
        if (isMountedRef.current) setPartnershipCentersData(centers);
      }

      // Initialize loaders
      const loaderObject = {};
      finalStates.forEach((st) => {
        loaderObject[st.hm_state] = true;
      });

      if (category === 1 && isMountedRef.current)
        setOwnLoading(loaderObject);
      else if (category === 2 && isMountedRef.current)
        setPartnershipLoading(loaderObject);

      // Fetch widgets ONLY for finalStates
      finalStates.forEach((st) =>
        fetchWidgetData(st.hm_state, category)
      );

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log(`Request aborted for category ${category}`);
      } else {
        console.error("Error loading states", error);
      }
    } finally {
      delete abortControllersRef.current[`fetchStates_${category}`];
    }
  };

  const fetchWidgetData = async (stateName, category) => {
    try {
      const controller = new AbortController();
      const key = `fetchWidget_${category}_${stateName}`;
      abortControllersRef.current[key] = controller;

      const res = await AxiosInstance.post("/dashboard/widget_count", {
        hm_state: stateName,
        ht_id: category,
        startdate: startDate,
        enddate: endDate,
      }, {
        signal: controller.signal
      });

      if (!isMountedRef.current) return;

      if (category === 1) {
        setOwnWidgets((prev) => ({
          ...prev,
          [stateName]: res.data.data,
        }));
      } else {
        setPartnershipWidgets((prev) => ({
          ...prev,
          [stateName]: res.data.data,
        }));
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log(`Widget request aborted for ${stateName}`);
      } else {
        console.error("Error loading widget", err);
      }
    } finally {
      if (!isMountedRef.current) return;

      // Ensure loader always stops
      if (category === 1) {
        setOwnLoading((prev) => ({
          ...prev,
          [stateName]: false,
        }));
      } else {
        setPartnershipLoading((prev) => ({
          ...prev,
          [stateName]: false,
        }));
      }

      delete abortControllersRef.current[`fetchWidget_${category}_${stateName}`];
    }
  };

  const showCenter = (category, stateName) => {
    //filter centers based on state name from their category
    if (category === 1) {
      //filter own centers form ownCenter data
      const filteredCenters = OwnCentersData.filter((center) => center.hm_state === stateName);
      setAllCentersData(filteredCenters);
    } else if (category === 2) {
      //filter partnership centers form partnershipCenter data
      const filteredCenters = PartnershipCentersData.filter((center) => center.hm_state === stateName);
      setAllCentersData(filteredCenters);
    }
    setShowCenterModal(true);
  }

  const fetchModalData = async (center_type, stateName, category) => {
    try {
      setModalData([]);
      setModalLoading(true);
      setDataCategory(category);

      const controller = new AbortController();
      const key = `fetchModal_${category}_${stateName}`;
      abortControllersRef.current[key] = controller;

      if (category === "Leads") {
        setLeadModel(true);
        const res = await AxiosInstance.post("/dashboard/widget_details", {
          hm_state: stateName,
          center_type: center_type,
          category: category,
          startdate: startDate,
          enddate: endDate,
        }, {
          signal: controller.signal
        });

        if (isMountedRef.current) {
          const flatLeads = (res?.data?.data ?? []).flatMap(source =>
            (source?.total_leads ?? []).map(lead => ({
              ...lead,
              source_name: source.source_name
            }))
          );
          setModalData(flatLeads);
        }
      }
      else if (category === "Appointments") {
        setAppointmentModel(true);
        const res = await AxiosInstance.post("/dashboard/widget_details", {
          hm_state: stateName,
          center_type: center_type,
          category: category,
          startdate: startDate,
          enddate: endDate,
        }, {
          signal: controller.signal
        });

        if (isMountedRef.current) {
          setModalData(res?.data?.data ?? []);
        }
      }
      else if (category === "Patients New" || category === "Patients Running" || category === "Patients Treated" || category === "Patients Completed") {
        setPatientModel(true);
        setPatientModalHeader(category);
        setIsPatientModalFromTotal(stateName === "Total");
        const res = await AxiosInstance.post("/dashboard/widget_details", {
          hm_state: stateName,
          center_type: center_type,
          category: category,
          startdate: startDate,
          enddate: endDate,
        }, {
          signal: controller.signal
        });

        if (isMountedRef.current) {
          setModalData(res?.data?.data ?? []);
        }
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        console.log(`Modal request aborted for ${category}`);
      } else {
        console.error("Error loading modal data", err);
      }
    } finally {
      if (isMountedRef.current) {
        setModalLoading(false);
      }
      delete abortControllersRef.current[`fetchModal_${category}`];
    }
  };

  const DetailItem = ({ type, sessions, status, dateLabel, date, last_treatment_date = null }) => (
    <div
      className="border rounded px-2 py-1 mb-1 bg-white"
    >
      <span className="fw-semibold">{type}-{sessions} ({status})</span><br />
      <span className="text-muted small">{dateLabel}: {date}</span>
      {last_treatment_date && (
        <>
          <br />
          <span className="text-muted small">Last Treatment: {last_treatment_date}</span>
        </>
      )}
    </div>
  );

  const toggleFullScreenModal = () => {
    setIsFullScreenModal(prev => !prev);
  };

  const getCenterWiseCounts = () => {
    if (!modalData || modalData.length === 0) return {};

    const centerCounts = {};

    // For Leads and Appointments - use center_name or hm_name
    if (dataCategory === "Leads" || dataCategory === "Appointments") {
      modalData.forEach(item => {
        const centerName = dataCategory === "Leads" ? item.center_name : item.hm_name;
        if (centerName) {
          centerCounts[centerName] = (centerCounts[centerName] || 0) + 1;
        }
      });
    }
    // For Patients - use center_name
    else if (dataCategory === "Patients New" || dataCategory === "Patients Running" || dataCategory === "Patients Treated" || dataCategory === "Patients Completed") {
      modalData.forEach(patient => {
        const centerName = patient.center_name;
        if (centerName) {
          centerCounts[centerName] = (centerCounts[centerName] || 0) + 1;
        }
      });
    }

    return centerCounts;
  };

  /** When modal is from Total, return one aggregated counts object (no center split). */
  const getTotalPackageCounts = () => {
    const byCenter = getPackageCountsByCenter();
    const total = {
      totalPackageCount: 0,
      sdtBoosterCount: 0,
      sdtFullPackageCount: 0,
      snbBoosterCount: 0,
      snbFullPackageCount: 0,
      totalSnbSessions: 0,
      totalSdtSessions: 0,
      totalSnbBoosterSessions: 0,
      totalSdtBoosterSessions: 0,
    };
    Object.values(byCenter).forEach((counts) => {
      total.totalPackageCount += counts.totalPackageCount;
      total.sdtBoosterCount += counts.sdtBoosterCount;
      total.sdtFullPackageCount += counts.sdtFullPackageCount;
      total.snbBoosterCount += counts.snbBoosterCount;
      total.snbFullPackageCount += counts.snbFullPackageCount;
      total.totalSnbSessions += counts.totalSnbSessions;
      total.totalSdtSessions += counts.totalSdtSessions;
      total.totalSnbBoosterSessions += counts.totalSnbBoosterSessions;
      total.totalSdtBoosterSessions += counts.totalSdtBoosterSessions;
    });
    return total;
  };

  const getPackageCountsByCenter = () => {
    if (!modalData || modalData.length === 0) return {};

    const centerCounts = {};

    // Only process for patient categories
    if (dataCategory === "Patients New" || dataCategory === "Patients Running" || dataCategory === "Patients Treated" || dataCategory === "Patients Completed") {
      modalData.forEach(patient => {
        const centerName = patient.center_name;
        if (!centerName) return;

        // Initialize center if not exists
        if (!centerCounts[centerName]) {
          centerCounts[centerName] = {
            totalPackageCount: 0,
            sdtBoosterCount: 0,
            sdtFullPackageCount: 0,
            snbBoosterCount: 0,
            snbFullPackageCount: 0,
            totalSnbSessions: 0,
            totalSdtSessions: 0,
            totalSnbBoosterSessions: 0,
            totalSdtBoosterSessions: 0
          };
        }

        const items = patient[config.key] || [];

        items.forEach(item => {
          centerCounts[centerName].totalPackageCount += 1;

          const packageType = config.getType(item); // "SDT" or "SNB"
          const sessions = config.getSessions(item); // total sessions

          if (packageType === "SDT") {
            centerCounts[centerName].totalSdtSessions += sessions;
            // If sessions not equal to 20, it's a booster
            if (sessions !== 20) {
              centerCounts[centerName].sdtBoosterCount += 1;
              centerCounts[centerName].totalSdtBoosterSessions += sessions;
            } else {
              centerCounts[centerName].sdtFullPackageCount += 1;
            }
          } else if (packageType === "SNB") {
            centerCounts[centerName].totalSnbSessions += sessions;
            // If sessions not equal to 40, it's a booster
            if (sessions !== 40) {
              centerCounts[centerName].snbBoosterCount += 1;
              centerCounts[centerName].totalSnbBoosterSessions += sessions;
            } else {
              centerCounts[centerName].snbFullPackageCount += 1;
            }
          }
        });
      });
    }

    return centerCounts;
  };

  const printData = () => {
    if (!modalData || modalData.length === 0) {
      alert("No data to print");
      return;
    }

    const printTitle =
      dataCategory === "Patients New" || dataCategory === "Patients Running" ||
        dataCategory === "Patients Treated" || dataCategory === "Patients Completed"
        ? patientModalHeader
        : dataCategory;

    let tableHeaders = "";
    let tableRows = "";

    if (dataCategory === "Leads") {
      tableHeaders = "<tr><th>#</th><th>Lead Name</th><th>Phone Number</th><th>Age</th><th>Center Name</th><th>Primary Source</th><th>Status</th></tr>";
      modalData.forEach((lead, index) => {
        const source = lead.source_name === "Transfer from Other Center"
          ? `Transferred From ${lead.transferred_from}`
          : (lead.source_name || "");
        tableRows += `<tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(lead.name || "")}</td>
          <td>${escapeHtml(lead.phone || "")}</td>
          <td>${lead.dob ? getAge(lead.dob) : ""}</td>
          <td>${escapeHtml(lead.center_name || "")}</td>
          <td>${escapeHtml(source)}</td>
          <td>${LeadStatusMap[lead.status] || "Unknown"}</td>
        </tr>`;
      });
    } else if (dataCategory === "Appointments") {
      tableHeaders = "<tr><th>#</th><th>Patient Name</th><th>Patient ID</th><th>Age</th><th>Center Name</th><th>Dr. Name</th><th>Date</th></tr>";
      modalData.forEach((appointment, index) => {
        tableRows += `<tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(appointment.pm_name || "")}</td>
          <td>${escapeHtml(appointment.patient_unique_id || "")}</td>
          <td>${appointment.pm_dob ? getAge(appointment.pm_dob) : ""}</td>
          <td>${escapeHtml(appointment.hm_name || "")}</td>
          <td>${escapeHtml(appointment.doctor_name || "N/A")}</td>
          <td>${escapeHtml(appointment.pam_app_date || "Unknown")}</td>
        </tr>`;
      });
    } else if (dataCategory === "Patients New" || dataCategory === "Patients Running" || dataCategory === "Patients Treated" || dataCategory === "Patients Completed") {
      // For Patients Treated, include SNB and SDT treatment columns
      if (dataCategory === "Patients Treated") {
        tableHeaders = `<tr><th>#</th><th>Patient Name</th><th>PUID</th><th>Age</th><th>Phone</th><th>Center Name</th><th class="text-center">SNB Treatments</th><th class="text-center">SDT Treatments</th><th colSpan="2">${config.label}</th></tr>`;
        modalData.forEach((patient, index) => {
          const items = patient[config.key] || [];
          const name = escapeHtml(patient.patient_name || "");
          const puid = escapeHtml(patient.patient_unique_id || "N/A");
          const age = patient.pm_dob ? getAge(patient.pm_dob) : "N/A";
          const phone = escapeHtml(patient.phone || "N/A");
          const center = escapeHtml(patient.center_name || "");

          // Count SNB and SDT treatments
          let snbCount = 0;
          let sdtCount = 0;
          items.forEach(item => {
            const itemType = config.getType(item); // "SNB" or "SDT"
            if (itemType === "SNB") {
              snbCount++;
            } else if (itemType === "SDT") {
              sdtCount++;
            }
          });

          if (items.length === 0) {
            tableRows += `<tr><td>${index + 1}</td><td>${name}</td><td>${puid}</td><td>${age}</td><td>${phone}</td><td>${center}</td><td class="text-center">0</td><td class="text-center">0</td><td colspan="2">—</td></tr>`;
          } else {
            const first = items[0];
            const detail1 = `${config.getType(first)}-${config.getSessions(first)} (${config.getStatus(first)}) ${config.dateLabel}: ${config.getDate(first)}`;
            tableRows += `<tr><td>${index + 1}</td><td>${name}</td><td>${puid}</td><td>${age}</td><td>${phone}</td><td>${center}</td><td class="text-center">${snbCount}</td><td class="text-center">${sdtCount}</td><td colspan="2">${escapeHtml(detail1)}</td></tr>`;
            for (let i = 1; i < items.length; i++) {
              const item = items[i];
              const detail = `${config.getType(item)}-${config.getSessions(item)} (${config.getStatus(item)}) ${config.dateLabel}: ${config.getDate(item)}`;
              tableRows += `<tr><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td colspan="2">${escapeHtml(detail)}</td></tr>`;
            }
          }
        });
      } else {
        tableHeaders = `<tr><th>#</th><th>Patient Name</th><th>PUID</th><th>Age</th><th>Phone</th><th>Center Name</th><th colSpan="2">${config.label}</th></tr>`;
        modalData.forEach((patient, index) => {
          const items = patient[config.key] || [];
          const name = escapeHtml(patient.patient_name || "");
          const puid = escapeHtml(patient.patient_unique_id || "N/A");
          const age = patient.pm_dob ? getAge(patient.pm_dob) : "N/A";
          const phone = escapeHtml(patient.phone || "N/A");
          const center = escapeHtml(patient.center_name || "");
          if (items.length === 0) {
            tableRows += `<tr><td>${index + 1}</td><td>${name}</td><td>${puid}</td><td>${age}</td><td>${phone}</td><td>${center}</td><td colspan="2">—</td></tr>`;
          } else {
            const first = items[0];
            const detail1 = `${config.getType(first)}-${config.getSessions(first)} (${config.getStatus(first)}) ${config.dateLabel}: ${config.getDate(first)}`;
            tableRows += `<tr><td>${index + 1}</td><td>${name}</td><td>${puid}</td><td>${age}</td><td>${phone}</td><td>${center}</td><td colspan="2">${escapeHtml(detail1)}</td></tr>`;
            for (let i = 1; i < items.length; i++) {
              const item = items[i];
              const detail = `${config.getType(item)}-${config.getSessions(item)} (${config.getStatus(item)}) ${config.dateLabel}: ${config.getDate(item)}`;
              tableRows += `<tr><td></td><td></td><td></td><td></td><td></td><td></td><td colspan="2">${escapeHtml(detail)}</td></tr>`;
            }
          }
        });
      }
    } else {
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow pop-ups to print.");
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${escapeHtml(printTitle)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; }
            h2 { margin-bottom: 9px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #f5f5f5; }
            p{ margin: 2px 0px; }
          </style>
        </head>
        <body>
          <h2>${escapeHtml(printTitle)}</h2>
          <p>${escapeHtml(centerOrGroupName)}</p>
          <div style="display: flex; justify-content: space-between;">
           <p>Selected Date Range  ${formatedDate(startDate) + " to " + formatedDate(endDate)}</p>
           <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
          <table>
            <thead>${tableHeaders}</thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  };

  const escapeHtml = (text) => {
    if (text == null) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  const downloadCsv = () => {
    if (!modalData || modalData.length === 0) {
      alert("No data to download");
      return;
    }

    let csvContent = "";
    let fileName = "data.csv";

    // Handle Leads Modal
    if (dataCategory === "Leads") {
      fileName = "leads.csv";
      const headers = ["#", "Lead Name", "Phone Number", "Age", "Center Name", "Primary Source", "Status"];
      csvContent = headers.join(",") + "\n";

      modalData.forEach((lead, index) => {
        const source = lead.source_name === "Transfer from Other Center"
          ? `Transferred From ${lead.transferred_from}`
          : lead.source_name;
        const row = [
          index + 1,
          `"${lead.name || ""}"`,
          `"${lead.phone || ""}"`,
          `"${lead.dob ? getAge(lead.dob) : '' || ""}"`,
          `"${lead.center_name || ""}"`,
          `"${source || ""}"`,
          LeadStatusMap[lead.status] || "Unknown"
        ];
        csvContent += row.join(",") + "\n";
      });
    }
    // Handle Appointments Modal
    else if (dataCategory === "Appointments") {
      fileName = "appointments.csv";
      const headers = ["#", "Patient Name", "Patient ID", "Age", "Center Name", "Dr. Name", "Date"];
      csvContent = headers.join(",") + "\n";

      modalData.forEach((appointment, index) => {
        const row = [
          index + 1,
          `"${appointment.pm_name || ""}"`,
          `"${appointment.patient_unique_id || ""}"`,
          `"${appointment.pm_dob ? getAge(appointment.pm_dob) : '' || ""}"`,
          `"${appointment.hm_name || ""}"`,
          `"${appointment.doctor_name || "N/A"}"`,
          `"${appointment.pam_app_date || "Unknown"}"`
        ];
        csvContent += row.join(",") + "\n";
      });
    }
    // Handle Patient Modal
    else if (dataCategory === "Patients New" || dataCategory === "Patients Running" || dataCategory === "Patients Treated" || dataCategory === "Patients Completed") {
      fileName = `patients-${dataCategory.toLowerCase().replace(/ /g, "-")}.csv`;

      // For Patients Treated, include SNB and SDT treatment columns
      if (dataCategory === "Patients Treated") {
        const headers = ["#", "Patient Name", "PUID", "Age", "Phone", "Center Name", "SNB Treatments", "SDT Treatments", "Type", "Sessions", "Status", "Date"];
        csvContent = headers.join(",") + "\n";

        modalData.forEach((patient, index) => {
          const items = patient[config.key] || [];

          // Count SNB and SDT treatments
          let snbCount = 0;
          let sdtCount = 0;
          items.forEach(item => {
            const itemType = config.getType(item);
            if (itemType === "SNB") {
              snbCount++;
            } else if (itemType === "SDT") {
              sdtCount++;
            }
          });

          if (items.length === 0) {
            const row = [
              index + 1,
              `"${patient.patient_name || ""}"`,
              `"${patient.patient_unique_id || "N/A"}"`,
              `"${patient.pm_dob ? getAge(patient.pm_dob) : '' || "N/A"}"`,
              `"${patient.phone || "N/A"}"`,
              `"${patient.center_name || ""}"`,
              0,
              0,
              "", "", "", ""
            ];
            csvContent += row.join(",") + "\n";
          } else {
            const firstItem = items[0];
            const row = [
              index + 1,
              `"${patient.patient_name || ""}"`,
              `"${patient.patient_unique_id || "N/A"}"`,
              `"${patient.pm_dob ? getAge(patient.pm_dob) : '' || "N/A"}"`,
              `"${patient.phone || "N/A"}"`,
              `"${patient.center_name || ""}"`,
              snbCount,
              sdtCount,
              config.getType(firstItem),
              config.getSessions(firstItem),
              config.getStatus(firstItem),
              config.getDate(firstItem)
            ];
            csvContent += row.join(",") + "\n";

            // Additional rows for remaining items
            for (let i = 1; i < items.length; i++) {
              const item = items[i];
              const additionalRow = [
                "", "", "", "", "", "", "", "",
                config.getType(item),
                config.getSessions(item),
                config.getStatus(item),
                config.getDate(item)
              ];
              csvContent += additionalRow.join(",") + "\n";
            }
          }
        });
      } else {
        const headers = ["#", "Patient Name", "PUID", "Age", "Phone", "Center Name", "Type", "Sessions", "Status", "Date"];
        csvContent = headers.join(",") + "\n";

        modalData.forEach((patient, index) => {
          const items = patient[config.key] || [];

          if (items.length === 0) {
            // Row without details
            const row = [
              index + 1,
              `"${patient.patient_name || ""}"`,
              `"${patient.patient_unique_id || "N/A"}"`,
              `"${patient.pm_dob ? getAge(patient.pm_dob) : '' || "N/A"}"`,
              `"${patient.phone || "N/A"}"`,
              `"${patient.center_name || ""}"`,
              "", "", "", ""
            ];
            csvContent += row.join(",") + "\n";
          } else {
            // First row with first item details
            const firstItem = items[0];
            const row = [
              index + 1,
              `"${patient.patient_name || ""}"`,
              `"${patient.patient_unique_id || "N/A"}"`,
              `"${patient.pm_dob ? getAge(patient.pm_dob) : '' || "N/A"}"`,
              `"${patient.phone || "N/A"}"`,
              `"${patient.center_name || ""}"`,
              config.getType(firstItem),
              config.getSessions(firstItem),
              config.getStatus(firstItem),
              config.getDate(firstItem)
            ];
            csvContent += row.join(",") + "\n";

            // Additional rows for remaining items
            for (let i = 1; i < items.length; i++) {
              const item = items[i];
              const additionalRow = [
                "", "", "", "", "", "",
                config.getType(item),
                config.getSessions(item),
                config.getStatus(item),
                config.getDate(item)
              ];
              csvContent += additionalRow.join(",") + "\n";
            }
          }
        });
      }
    }

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const getAge = (dob) => {
    if (!dob) return 0;

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }


    return age + ' Years';

  };

  return (
    <>
      <div className="" style={{ "paddingBottom": "100px" }}>
        {/* <Link to="/center-group-overview" className={`me-3 text-decoration-underline ${currentPath === '/center-group-overview' ? 'text-primary' : ''}`}>Group Overview</Link>
        <Link to="/" className={`mb-3 text-decoration-underline ${currentPath === '/' ? 'text-primary' : ''}`}>State Overview</Link> */}
        <div className="row gy-3 pb-4">
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 d-none d-lg-block">
            <div
              className="rounded-4 shadow-lg border"
              style={{
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Header */}
              <div
                className="p-3 text-white rounded-top-4"
                style={{
                  background:
                    "linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex justify-content-center align-items-center rounded-circle me-3"
                    style={{
                      width: "35px",
                      height: "35px",
                      background: "rgba(255,255,255,0.2)",
                    }}
                  >
                    <MapPin size={20} />
                  </div>

                  <span className="mb-0 fw-bold" style={{ fontSize: "22px" }}>Own</span>
                </div>
              </div>



              {/* Data */}
              <div className="p-3">
                <div

                  className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                  style={{
                    background: "white",
                    cursor: "pointer",
                    transition: "0.3s",
                    columnGap: "10px"
                  }}
                >
                  {/* Left */}
                  <div className='d-flex align-items-center' >

                    <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Leads</span>
                  </div>
                </div>

                <div

                  className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                  style={{
                    background: "white",
                    cursor: "pointer",
                    transition: "0.3s",
                    columnGap: "10px"
                  }}
                >
                  {/* Left */}
                  <div className='d-flex align-items-center' >

                    <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Appointments</span>
                  </div>
                </div>
                <div

                  className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                  style={{
                    background: "white",
                    cursor: "pointer",
                    transition: "0.3s",
                    columnGap: "10px"
                  }}
                >
                  {/* Left */}
                  <div className='d-flex align-items-center' >

                    <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Patient New</span>
                  </div>
                </div>
                <div

                  className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                  style={{
                    background: "white",
                    cursor: "pointer",
                    transition: "0.3s",
                    columnGap: "10px"
                  }}
                >
                  {/* Left */}
                  <div className='d-flex align-items-center' >

                    <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Patients Running</span>
                  </div>
                </div>
                <div

                  className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                  style={{
                    background: "white",
                    cursor: "pointer",
                    transition: "0.3s",
                    columnGap: "10px"
                  }}
                >
                  {/* Left */}
                  <div className='d-flex align-items-center' >

                    <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Patients Completed</span>
                  </div>
                </div>
                <div

                  className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                  style={{
                    background: "white",
                    cursor: "pointer",
                    transition: "0.3s",
                    columnGap: "10px"
                  }}
                >
                  {/* Left */}
                  <div className='d-flex align-items-center' >

                    <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Patients Treated</span>
                  </div>
                </div>

              </div>

            </div>
          </div>
          {ownStates.map((st, index) => {
            const CENTER_NAME = st.hm_state;
            const widget = ownWidgets[CENTER_NAME];

            return (
              <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6" key={index} >
                <div
                  className="rounded-4 shadow-lg border"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  {/* Header */}
                  <div
                    className="p-3 text-white rounded-top-4"
                    style={{
                      background:
                        "linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)",
                      cursor: "pointer"
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex justify-content-center align-items-center rounded-circle me-3"
                        style={{
                          width: "35px",
                          height: "35px",
                          background: "rgba(255,255,255,0.2)",
                        }}
                      >
                        <MapPin size={20} />
                      </div>

                      <span className="mb-0 fw-bold" onClick={() => showCenter(1, CENTER_NAME)} style={{ fontSize: "20px" }}>{CENTER_NAME}</span>
                    </div>
                  </div>

                  {/* Loader */}
                  {ownLoading[CENTER_NAME] && (
                    <div className="p-4 text-center">
                      <div className="spinner-border text-primary"></div>
                      <p className="mt-2 mb-0 fw-bold">Loading...</p>
                    </div>
                  )}

                  {/* Data */}
                  {!ownLoading[CENTER_NAME] && widget && (
                    <div className="p-3">
                      {[
                        {
                          title: "Leads",
                          value: widget.total_leads,
                          color: "#0d6efd",
                        },
                        {
                          title: "Appointments",
                          value: widget.completed_appointments,
                          color: "#198754",
                        },
                        {
                          title: "Patients New",
                          value: widget.booked_package_patients,
                          color: "#dc3545",
                        },
                        {
                          title: "Patients Running",
                          value: widget.running_package_patients,
                          color: "#6f42c1",
                        },
                        {
                          title: "Patients Completed",
                          value: widget.completed_package_patients,
                          color: "#39504dff",
                        },
                        {
                          title: "Patients Treated",
                          value: widget.treated_patients,
                          color: "#e97b15ff",
                        },
                      ].map((m, i) => (
                        <div
                          key={i}
                          className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                          style={{
                            background: "white",
                            cursor: "pointer",
                            transition: "0.3s",
                            columnGap: "10px"
                          }}
                          onClick={() => {
                            fetchModalData(1, CENTER_NAME, m.title);
                            setCenterOrGroupName(CENTER_NAME + " (Own)" || "");
                          }
                          }
                        >
                          {/* Left */}
                          <div className='d-flex align-items-center' >
                            <div className="d-lg-none fw-bold">
                              {m.title}
                            </div>

                          </div>

                          <span className="fw-bold" style={{ fontSize: "18px", color: m.color }}>
                            {m.value ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* OWN CENTERS TOTAL WIDGET */}
          <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6" >
            <div
              className="rounded-4 shadow-lg border"
              style={{
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(10px)",

              }}
            >
              {/* Header */}
              <div
                className="p-3 text-white rounded-top-4"
                style={{
                  background:
                    "linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)",

                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex justify-content-center align-items-center rounded-circle me-3"
                    style={{
                      width: "35px",
                      height: "35px",
                      background: "rgba(255,255,255,0.2)",
                    }}
                  >
                    <MapPin size={20} />
                  </div>

                  <span className="mb-0 fw-bold" style={{ fontSize: "22px" }}>Total</span>
                </div>
              </div>

              {/* Data */}
              <div className="p-3">
                {[
                  {
                    title: "Leads",
                    value: Object.values(ownWidgets).reduce((sum, w) => sum + (w?.total_leads || 0), 0),

                    color: "#0d6efd",
                  },
                  {
                    title: "Appointments",
                    value: Object.values(ownWidgets).reduce((sum, w) => sum + (w?.completed_appointments || 0), 0),

                    color: "#198754",
                  },
                  {
                    title: "Patients New",
                    value: Object.values(ownWidgets).reduce((sum, w) => sum + (w?.booked_package_patients || 0), 0),

                    color: "#dc3545",
                  },
                  {
                    title: "Patients Running",
                    value: Object.values(ownWidgets).reduce((sum, w) => sum + (w?.running_package_patients || 0), 0),

                    color: "#6f42c1",
                  },
                  {
                    title: "Patients Completed",
                    value: Object.values(ownWidgets).reduce((sum, w) => sum + (w?.completed_package_patients || 0), 0),
                    color: "#39504dff",
                  },
                  {
                    title: "Patients Treated",
                    value: Object.values(ownWidgets).reduce((sum, w) => sum + (w?.treated_patients || 0), 0),

                    color: "#e97b15ff",
                  },
                ].map((m, i) => (
                  <div
                    key={i}
                    className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                    style={{
                      background: "white",
                      cursor: "pointer",
                      transition: "0.3s",
                      columnGap: "10px"
                    }}
                    onClick={() => {
                      fetchModalData(1, "Total", m.title);
                      setCenterOrGroupName('Own Centers' || "");
                    }
                    }
                  >
                    <div className='d-flex align-items-center' >
                      <div className="d-lg-none fw-bold ">
                        {m.title}
                      </div>

                    </div>
                    <span className="fw-bold" style={{ fontSize: "18px", color: m.color }}>
                      {m.value ?? 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* HORIZONTAL RULER SEPARATOR */}
          <div className="w-100 my-4" style={{ borderTop: '3px solid #4f46e5', opacity: 0.5 }}></div>

          {/* PARTNERSHIP SECTION HEADER */}
          {partnershipStates.length > 0 && (
            <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 d-none d-lg-block">
              <div
                className="rounded-4 shadow-lg border"
                style={{
                  background: "rgba(255,255,255,0.92)",
                  backdropFilter: "blur(10px)",
                }}
              >
                {/* Header */}
                <div
                  className="p-3 text-white rounded-top-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #fb923c 100%)",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex justify-content-center align-items-center rounded-circle me-3"
                      style={{
                        width: "35px",
                        height: "35px",
                        background: "rgba(255,255,255,0.2)",
                      }}
                    >
                      <MapPin size={20} />
                    </div>

                    <span className="mb-0 fw-bold" style={{ fontSize: "22px" }}>Partnership</span>
                  </div>
                </div>



                {/* Data */}
                <div className="p-3">
                  <div

                    className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                    style={{
                      background: "white",
                      cursor: "pointer",
                      transition: "0.3s",
                      columnGap: "10px"
                    }}
                  >
                    {/* Left */}
                    <div className='d-flex align-items-center' >

                      <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Leads</span>
                    </div>



                  </div>

                  <div

                    className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                    style={{
                      background: "white",
                      cursor: "pointer",
                      transition: "0.3s",
                      columnGap: "10px"
                    }}
                  >
                    {/* Left */}
                    <div className='d-flex align-items-center' >

                      <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Appointments</span>
                    </div>
                  </div>
                  <div

                    className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                    style={{
                      background: "white",
                      cursor: "pointer",
                      transition: "0.3s",
                      columnGap: "10px"
                    }}
                  >
                    {/* Left */}
                    <div className='d-flex align-items-center' >

                      <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Patient New</span>
                    </div>
                  </div>
                  <div

                    className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                    style={{
                      background: "white",
                      cursor: "pointer",
                      transition: "0.3s",
                      columnGap: "10px"
                    }}
                  >
                    {/* Left */}
                    <div className='d-flex align-items-center' >

                      <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Patients Running</span>
                    </div>
                  </div>

                  <div

                    className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                    style={{
                      background: "white",
                      cursor: "pointer",
                      transition: "0.3s",
                      columnGap: "10px"
                    }}
                  >
                    {/* Left */}
                    <div className='d-flex align-items-center' >

                      <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Patients Completed</span>
                    </div>
                  </div>

                  <div

                    className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                    style={{
                      background: "white",
                      cursor: "pointer",
                      transition: "0.3s",
                      columnGap: "10px"
                    }}
                  >
                    {/* Left */}
                    <div className='d-flex align-items-center' >

                      <span className="fw-bold mb-0" style={{ fontSize: "18px" }}>Patients Treated</span>
                    </div>
                  </div>

                </div>

              </div>
            </div>
          )}

          {/* PARTNERSHIP WIDGETS */}
          {partnershipStates.map((st, index) => {
            const CENTER_NAME = st.hm_state;
            const widget = partnershipWidgets[CENTER_NAME];

            return (
              <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6" key={`partnership-${index}`} >
                <div
                  className="rounded-4 shadow-lg border"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  {/* Header */}
                  <div
                    className="p-3 text-white rounded-top-4"
                    style={{
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #fb923c 100%)",
                      cursor: "pointer"
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className="d-flex justify-content-center align-items-center rounded-circle me-3"
                        style={{
                          width: "35px",
                          height: "35px",
                          background: "rgba(255,255,255,0.2)",
                        }}
                      >
                        <MapPin size={20} />
                      </div>

                      <span className="mb-0 fw-bold" onClick={() => showCenter(2, CENTER_NAME)} style={{ fontSize: "20px" }}>{CENTER_NAME}</span>
                    </div>
                  </div>

                  {/* Loader */}
                  {partnershipLoading[CENTER_NAME] && (
                    <div className="p-4 text-center">
                      <div className="spinner-border text-primary"></div>
                      <p className="mt-2 mb-0 fw-bold">Loading...</p>
                    </div>
                  )}

                  {/* Data */}
                  {!partnershipLoading[CENTER_NAME] && widget && (
                    <div className="p-3">
                      {[
                        {
                          title: "Leads",
                          value: widget.total_leads,

                          color: "#0d6efd",
                        },
                        {
                          title: "Appointments",
                          value: widget.completed_appointments,

                          color: "#198754",
                        },
                        {
                          title: "Patients New",
                          value: widget.booked_package_patients,

                          color: "#dc3545",
                        },
                        {
                          title: "Patients Running",
                          value: widget.running_package_patients,

                          color: "#6f42c1",
                        },
                        {
                          title: "Patients Completed",
                          value: widget.completed_package_patients,

                          color: "#39504dff",
                        },
                        {
                          title: "Patients Treated",
                          value: widget.treated_patients,

                          color: "#e97b15ff",
                        },
                      ].map((m, i) => (
                        <div
                          key={i}
                          className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                          style={{
                            background: "white",
                            cursor: "pointer",
                            transition: "0.3s",
                            columnGap: "10px"
                          }}
                        >
                          {/* Left */}
                          <div className='d-flex align-items-center' >
                            <div className="d-lg-none fw-bold">
                              {m.title}
                            </div>
                          </div>


                          <span className="fw-bold"
                            onClick={() => {
                              fetchModalData(2, CENTER_NAME, m.title);
                              setCenterOrGroupName(CENTER_NAME + " (PartnerShip)" || "");
                            }
                            }
                            style={{ fontSize: "18px", color: m.color }}>
                            {m.value ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* PARTNERSHIP TOTAL WIDGET */}
          {partnershipStates.length > 0 && (
            <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6" >
              <div
                className="rounded-4 shadow-lg border"
                style={{
                  background: "rgba(255,255,255,0.92)",
                  backdropFilter: "blur(10px)",
                }}
              >
                {/* Header */}
                <div
                  className="p-3 text-white rounded-top-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #fb923c 100%)",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex justify-content-center align-items-center rounded-circle me-3"
                      style={{
                        width: "35px",
                        height: "35px",
                        background: "rgba(255,255,255,0.2)",
                      }}
                    >
                      <MapPin size={20} />
                    </div>

                    <span className="mb-0 fw-bold" style={{ fontSize: "22px" }}>Total</span>
                  </div>
                </div>

                {/* Data */}
                <div className="p-3">
                  {[
                    {
                      title: "Leads",
                      value: Object.values(partnershipWidgets).reduce((sum, w) => sum + (w?.total_leads || 0), 0),

                      color: "#0d6efd",
                    },
                    {
                      title: "Appointments",
                      value: Object.values(partnershipWidgets).reduce((sum, w) => sum + (w?.completed_appointments || 0), 0),

                      color: "#198754",
                    },
                    {
                      title: "Patients New",
                      value: Object.values(partnershipWidgets).reduce((sum, w) => sum + (w?.booked_package_patients || 0), 0),

                      color: "#dc3545",
                    },
                    {
                      title: "Patients Running",
                      value: Object.values(partnershipWidgets).reduce((sum, w) => sum + (w?.running_package_patients || 0), 0),

                      color: "#6f42c1",
                    },
                    {
                      title: "Patients Completed",
                      value: Object.values(partnershipWidgets).reduce((sum, w) => sum + (w?.completed_package_patients || 0), 0),
                      color: "#39504dff",
                    },
                    {
                      title: "Patients Treated",
                      value: Object.values(partnershipWidgets).reduce((sum, w) => sum + (w?.treated_patients || 0), 0),

                      color: "#e97b15ff",
                    },
                  ].map((m, i) => (
                    <div
                      key={i}
                      className="d-flex justify-content-between align-items-center p-2 mb-3 rounded-3 shadow-sm"
                      style={{
                        background: "white",
                        cursor: "pointer",
                        transition: "0.3s",
                        columnGap: "10px"
                      }}
                      onClick={() => {
                        fetchModalData(2, "Total", m.title);
                        setCenterOrGroupName("PartnerShip Centers" || "");
                      }
                      }
                    >
                      <div className='d-flex align-items-center' >
                        <div className="d-lg-none fw-bold">
                          {m.title}
                        </div>
                      </div>
                      <span className="fw-bold" style={{ fontSize: "18px", color: m.color }}>
                        {m.value ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
      {showCenterModal && (
        <>
          <div className="modal-backdrop fade show w-100 h-100"></div>
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ position: "fixed", overflowY: "auto" }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Center Names</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCenterModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div>
                    <table className="table table-bordered">
                      <thead
                        style={{
                          position: "sticky",
                          top: -18,
                          backgroundColor: "white",
                          zIndex: 1,
                        }}
                      >

                        <tr>
                          <th>Center Name</th>

                        </tr>
                      </thead>
                      <tbody>

                        {allCentersData && (
                          allCentersData
                            .map((center, index) => (
                              <tr key={index}>
                                <td>{center.hm_name}</td>
                              </tr>
                            ))
                        )}

                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )
      }


      {leadModel && (
        <>
          {/* Backdrop */}
          <div className="modal-backdrop fade show w-100 h-100"></div>

          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ position: "fixed", overflowY: "auto" }}
          >
            <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
              <div className="modal-content">

                {/* Header */}
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title">Lead Details</h5>
                  <div className="d-flex align-items-center">
                    {selectedLeadStatus && (
                      <button
                        className="btn btn-sm btn-secondary me-2"
                        onClick={() => setSelectedLeadStatus("")}
                        title="Reset filters"
                      >
                        Reset Filters
                      </button>
                    )}
                    <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                      onClick={printData} >
                      <i className="ri-file-pdf-2-line"></i>

                    </div>
                    {/* download csv button */}
                    <div className="cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                      onClick={downloadCsv} >
                      <i className="ri-file-excel-2-line"></i>

                    </div>

                    <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                      onClick={toggleFullScreenModal} >
                      {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setLeadModel(false);
                        setSelectedLeadStatus("");
                      }}
                    ></button>
                  </div>
                </div>

                {/* Body */}
                <div className="modal-body">
                  {/* Status Filter */}
                  {!modalLoading && modalData.length > 0 && (
                    <div className="mb-3">
                      <label className="form-label fw-bold">Filter by Status:</label>
                      <select
                        className="form-select"
                        value={selectedLeadStatus}
                        onChange={(e) => setSelectedLeadStatus(e.target.value)}
                      >
                        <option value="">All Statuses</option>
                        {Object.entries(LeadStatusMap).map(([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* center wise counting */}
                  {!modalLoading && modalData.length > 0 && (
                    <div className="mb-3 bg-light rounded">
                      <div className="d-flex flex-wrap gap-2">
                        {Object.entries(getCenterWiseCounts()).map(([centerName, count]) => (
                          <span key={centerName} className="badge bg-primary fs-6">
                            {centerName}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Loader */}
                  {modalLoading && (
                    <div className="p-4 text-center">
                      <div className="spinner-border text-primary"></div>
                      <p className="mt-2 mb-0 fw-bold">Loading...</p>
                    </div>
                  )}

                  {/* Table */}
                  {!modalLoading && modalData.length > 0 && (
                    <table className="table table-bordered">
                      <thead
                        style={{
                          position: "sticky",
                          top: -18,
                          backgroundColor: "white",
                          zIndex: 1,
                        }}
                      >
                        <tr>
                          <th>#</th>
                          <th>Lead Name</th>
                          <th>Phone Number</th>
                          <th>Age</th>
                          <th>Center Name</th>
                          <th>Primary Source</th>
                          <th>Status</th>
                        </tr>
                      </thead>

                      <tbody>
                        {modalData
                          .filter((lead) => selectedLeadStatus === "" || lead.status.toString() === selectedLeadStatus)
                          .map((lead, index) => (
                            <tr key={lead.lead_id}>
                              <td>{index + 1}</td>
                              <td>{lead.name}</td>
                              <td>{lead.phone}</td>
                              <td> {lead.dob ? getAge(lead.dob) : '-'} </td>
                              <td>{lead.center_name}</td>
                              <td>
                                {lead.source_name === "Transfer from Other Center"
                                  ? `Transferred From ${lead.transferred_from}`
                                  : lead.source_name}
                              </td>
                              <td>
                                {LeadStatusMap[lead.status] || "Unknown"}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}

                  {/* Empty State */}
                  {!modalLoading && modalData.length === 0 && (
                    <p className="text-center fw-bold">No leads found</p>
                  )}

                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {appointmentModel && (
        <>
          {/* Backdrop */}
          <div className="modal-backdrop fade show w-100 h-100"></div>

          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ position: "fixed", overflowY: "auto" }}
          >
            <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
              <div className="modal-content">

                {/* Header */}
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title">Appointments Details</h5>
                  <div className="d-flex align-items-center">
                    <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                      onClick={printData} >
                      <i className="ri-file-pdf-2-line"></i>

                    </div>
                    {/* download csv button */}
                    <div className="cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                      onClick={downloadCsv} >
                      <i className="ri-file-excel-2-line"></i>

                    </div>

                    <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                      onClick={toggleFullScreenModal} >
                      {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setAppointmentModel(false)}
                    ></button>
                  </div>
                </div>

                {/* Body */}
                <div className="modal-body">
                  {/* center wise counting */}
                  {!modalLoading && modalData.length > 0 && (
                    <div className="mb-3 bg-light rounded">
                      <div className="d-flex flex-wrap gap-2">
                        {Object.entries(getCenterWiseCounts()).map(([centerName, count]) => (
                          <span key={centerName} className="badge bg-primary fs-6">
                            {centerName}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Loader */}
                  {modalLoading && (
                    <div className="p-4 text-center">
                      <div className="spinner-border text-primary"></div>
                      <p className="mt-2 mb-0 fw-bold">Loading...</p>
                    </div>
                  )}

                  {/* Table */}
                  {!modalLoading && modalData.length > 0 && (
                    <table className="table table-bordered">
                      <thead
                        style={{
                          position: "sticky",
                          top: -18,
                          backgroundColor: "white",
                          zIndex: 1,
                        }}
                      >
                        <tr>
                          <th>#</th>
                          <th>Patient Name</th>
                          <th>Patient ID</th>
                          <th>Age</th>
                          <th>Center Name</th>
                          <th>Dr. Name</th>
                          <th>Date</th>
                        </tr>
                      </thead>

                      <tbody>
                        {modalData.map((appointment, index) => (
                          <tr key={appointment.pam_id}>
                            <td>{index + 1}</td>
                            <td>{appointment.pm_name}</td>
                            <td>{appointment.patient_unique_id}</td>
                            <td>{appointment.pm_dob ? getAge(appointment.pm_dob) : '-'}</td>
                            <td>{appointment.hm_name}</td>
                            <td>
                              {appointment.doctor_name || "N/A"}
                            </td>
                            <td>
                              {appointment.pam_app_date || "Unknown"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {/* Empty State */}
                  {!modalLoading && modalData.length === 0 && (
                    <p className="text-center fw-bold">No Appointments found</p>
                  )}

                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {patientModel && (
        <>
          {/* Backdrop */}
          <div className="modal-backdrop fade show w-100 h-100"></div>

          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ position: "fixed", overflowY: "auto" }}
          >
            <div className={`modal-dialog ${isFullScreenModal ? 'modal-fullscreen w-100' : 'modal-xl'} modal-dialog-centered modal-dialog-scrollable`}>
              <div className="modal-content">

                {/* Header */}
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title">{patientModalHeader}</h5>
                  <div className="d-flex align-items-center">
                    <div className=" cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                      onClick={printData} >
                      <i className="ri-file-pdf-2-line"></i>

                    </div>
                    {/* download csv button */}
                    <div className="cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center me-2"
                      onClick={downloadCsv} >
                      <i className="ri-file-excel-2-line"></i>

                    </div>

                    <div className='cursor-pointer w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                      onClick={toggleFullScreenModal} >
                      {isFullScreenModal ? <i className="ri-fullscreen-exit-line"></i> : <i className="ri-fullscreen-line"></i>}

                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setPatientModel(false)}
                    ></button>
                  </div>
                </div>

                {/* Body */}
                <div className="modal-body">
                  {/* center wise counting and package information */}
                  {!modalLoading && modalData.length > 0 && (
                    <div className="mb-3 bg-light rounded p-3">
                      {isPatientModalFromTotal ? (
                        (() => {
                          const counts = getTotalPackageCounts();
                          const patientCount = modalData.length;
                          const isPatientsTreated = dataCategory === "Patients Treated";
                          const isPatientsNew = dataCategory === "Patients New";
                          const totalTreatment = counts.totalPackageCount;
                          const sdtTreatment = counts.sdtBoosterCount + counts.sdtFullPackageCount;
                          const snbTreatment = counts.snbBoosterCount + counts.snbFullPackageCount;
                          const totalSnbBoosterSessions = counts.totalSnbBoosterSessions || 0;
                          const totalSdtBoosterSessions = counts.totalSdtBoosterSessions || 0;
                          const totalSnbSessions = counts.totalSnbSessions || 0;
                          const totalSdtSessions = counts.totalSdtSessions || 0;
                          const effectiveSnbBooster = (totalSnbBoosterSessions / 40).toFixed(2);
                          const effectiveSdtBooster = (totalSdtBoosterSessions / 20).toFixed(2);
                          const effectiveSNBPackages = (totalSnbSessions / 40).toFixed(2);
                          const effectiveSDTPackages = (totalSdtSessions / 20).toFixed(2);
                          return (
                            <>
                              <h6 className="fw-bold mb-3">Total (all centers)</h6>
                              <div className="border rounded p-3 bg-white">
                                <div className="d-flex flex-wrap gap-2">
                                  <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                    <small className="text-muted d-block">Total Patient</small>
                                    <h6 className="mb-0 text-dark fw-bold">{patientCount}</h6>
                                  </div>
                                  {!isPatientsTreated && (
                                    <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                      <small className="text-muted d-block">Total Package</small>
                                      <h6 className="mb-0 text-dark fw-bold">{counts.totalPackageCount}</h6>
                                    </div>
                                  )}
                                  {isPatientsTreated ? (
                                    <>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">Total Treatment</small>
                                        <h6 className="mb-0 text-dark fw-bold">{totalTreatment}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SNB Treatment</small>
                                        <h6 className="mb-0 text-success fw-bold">{snbTreatment}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SDT Treatment</small>
                                        <h6 className="mb-0 text-success fw-bold">{sdtTreatment}</h6>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SDT Booster</small>
                                        <h6 className="mb-0 text-danger fw-bold">{counts.sdtBoosterCount}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SDT Full Package</small>
                                        <h6 className="mb-0 text-success fw-bold">{counts.sdtFullPackageCount}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SNB Booster</small>
                                        <h6 className="mb-0 text-danger fw-bold">{counts.snbBoosterCount}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SNB Full Package</small>
                                        <h6 className="mb-0 text-success fw-bold">{counts.snbFullPackageCount}</h6>
                                      </div>
                                      {isPatientsNew && (
                                        <>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Total SNB Booster Sessions</small>
                                            <h6 className="mb-0 text-dark fw-bold">{totalSnbBoosterSessions}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Total SDT Booster Sessions</small>
                                            <h6 className="mb-0 text-dark fw-bold">{totalSdtBoosterSessions}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Effective SNB Booster Package</small>
                                            <h6 className="mb-0 text-primary fw-bold">{effectiveSnbBooster}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Effective SDT Booster Package</small>
                                            <h6 className="mb-0 text-primary fw-bold">{effectiveSdtBooster}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Effective SNB Packages</small>
                                            <h6 className="mb-0 text-primary fw-bold">{effectiveSNBPackages}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Effective SDT Packages</small>
                                            <h6 className="mb-0 text-primary fw-bold">{effectiveSDTPackages}</h6>
                                          </div>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </>
                          );
                        })()
                      ) : (
                        <>
                          <h6 className="fw-bold mb-3">Package Information by Center:</h6>
                          {Object.entries(getPackageCountsByCenter()).map(([centerName, counts]) => {
                            const patientCount = getCenterWiseCounts()[centerName] || 0;
                            const isPatientsTreated = dataCategory === "Patients Treated";
                            const isPatientsNew = dataCategory === "Patients New";

                            const totalTreatment = counts.totalPackageCount;
                            const sdtTreatment = counts.sdtBoosterCount + counts.sdtFullPackageCount;
                            const snbTreatment = counts.snbBoosterCount + counts.snbFullPackageCount;

                            const totalSnbBoosterSessions = counts.totalSnbBoosterSessions || 0;
                            const totalSdtBoosterSessions = counts.totalSdtBoosterSessions || 0;
                            const totalSnbSessions = counts.totalSnbSessions || 0;
                            const totalSdtSessions = counts.totalSdtSessions || 0;
                            const effectiveSnbBooster =
                              (totalSnbBoosterSessions / 40).toFixed(2);
                            const effectiveSdtBooster =
                              (totalSdtBoosterSessions / 20).toFixed(2);
                            const effectiveSNBPackages = (totalSnbSessions / 40).toFixed(2);
                            const effectiveSDTPackages = (totalSdtSessions / 20).toFixed(2);

                            return (
                              <div key={centerName} className="mb-3 border rounded p-3 bg-white">
                                <h6 className="fw-bold text-primary mb-2">{centerName}</h6>
                                <div className="d-flex flex-wrap gap-2">
                                  <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                    <small className="text-muted d-block">Total Patient</small>
                                    <h6 className="mb-0 text-dark fw-bold">{patientCount}</h6>
                                  </div>
                                  {!isPatientsTreated && (
                                    <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                      <small className="text-muted d-block">Total Package</small>
                                      <h6 className="mb-0 text-dark fw-bold">{counts.totalPackageCount}</h6>
                                    </div>
                                  )}

                                  {isPatientsTreated ? (
                                    <>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">Total Treatment</small>
                                        <h6 className="mb-0 text-dark fw-bold">{totalTreatment}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SNB Treatment</small>
                                        <h6 className="mb-0 text-success fw-bold">{snbTreatment}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SDT Treatment</small>
                                        <h6 className="mb-0 text-success fw-bold">{sdtTreatment}</h6>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SDT Booster</small>
                                        <h6 className="mb-0 text-danger fw-bold">{counts.sdtBoosterCount}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SDT Full Package</small>
                                        <h6 className="mb-0 text-success fw-bold">{counts.sdtFullPackageCount}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SNB Booster</small>
                                        <h6 className="mb-0 text-danger fw-bold">{counts.snbBoosterCount}</h6>
                                      </div>
                                      <div className="border rounded p-2" style={{ minWidth: '140px', backgroundColor: '#f8f9fa' }}>
                                        <small className="text-muted d-block">SNB Full Package</small>
                                        <h6 className="mb-0 text-success fw-bold">{counts.snbFullPackageCount}</h6>
                                      </div>

                                      {isPatientsNew && (
                                        <>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Total SNB Booster Sessions</small>
                                            <h6 className="mb-0 text-dark fw-bold">{totalSnbBoosterSessions}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Total SDT Booster Sessions</small>
                                            <h6 className="mb-0 text-dark fw-bold">{totalSdtBoosterSessions}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Effective SNB Booster Package</small>
                                            <h6 className="mb-0 text-primary fw-bold">{effectiveSnbBooster}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Effective SDT Booster Package</small>
                                            <h6 className="mb-0 text-primary fw-bold">{effectiveSdtBooster}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Effective SNB Packages</small>
                                            <h6 className="mb-0 text-primary fw-bold">{effectiveSNBPackages}</h6>
                                          </div>
                                          <div className="border rounded p-2" style={{ minWidth: '170px', backgroundColor: '#f8f9fa' }}>
                                            <small className="text-muted d-block">Effective SDT Packages</small>
                                            <h6 className="mb-0 text-primary fw-bold">{effectiveSDTPackages}</h6>
                                          </div>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}

                  {/* Loader */}
                  {modalLoading && (
                    <div className="p-4 text-center">
                      <div className="spinner-border text-primary"></div>
                      <p className="mt-2 mb-0 fw-bold">Loading...</p>
                    </div>
                  )}

                  {/* Table */}
                  {!modalLoading && modalData.length > 0 && (
                    <table className="table table-bordered">
                      <thead
                        style={{
                          position: "sticky",
                          top: -18,
                          backgroundColor: "white",
                          zIndex: 1,
                        }}
                      >
                        <tr>
                          <th>#</th>
                          <th>Patient Name</th>
                          <th>PUID</th>
                          <th>Age</th>
                          <th>Phone</th>
                          <th>Center Name</th>
                          {dataCategory === "Patients Treated" ? (
                            <>
                              <th>SNB Treatments</th>
                              <th>SDT Treatments</th>
                            </>
                          ) : ('')}
                          <th colSpan={2}>{config.label}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {modalData.map((patient, index) => {
                          const items = patient[config.key] || [];
                          const visibleItems = items.slice(0, 2);
                          const isExpanded = expandedRows[patient.pm_id];

                          return (
                            <React.Fragment key={patient.pm_id}>
                              {/* MAIN ROW */}
                              <tr>
                                <td>{index + 1}</td>
                                <td>{patient.patient_name}</td>
                                <td>{patient.patient_unique_id ?? "N/A"}</td>
                                <td>{patient.pm_dob ? getAge(patient.pm_dob) : '-'}</td>
                                <td>{patient.phone ?? "N/A"}</td>
                                <td>{patient.center_name}</td>
                                {dataCategory === "Patients Treated" ? (
                                  <>
                                    <td>{patient.treatment_count_snb || 0}</td>
                                    <td>{patient.treatment_count_sdt || 0}</td>
                                  </>) : ('')}

                                <td colSpan={2}>
                                  {visibleItems.map((item, i) => (
                                    <DetailItem
                                      key={i}
                                      type={config.getType(item)}
                                      sessions={config.getSessions(item)}
                                      status={config.getStatus(item)}
                                      dateLabel={config.dateLabel}
                                      date={config.getDate(item)}
                                      last_treatment_date={config.getLastTreatmentDate(item)}
                                    />
                                  ))}

                                  {items.length > 2 && (
                                    <div
                                      className="text-primary small fw-semibold cursor-pointer mt-1"
                                      onClick={() => toggleRow(patient.pm_id)}
                                    >
                                      {isExpanded
                                        ? "▲ Hide details"
                                        : `▼ View all (${items.length})`}
                                    </div>
                                  )}
                                </td>
                              </tr>

                              {/* COLLAPSED ROW */}
                              {isExpanded && (
                                <tr className="bg-light">
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td colSpan={2}>
                                    <div style={{ maxHeight: 200, overflowY: "auto" }}>
                                      {items.map((item, i) => (
                                        <DetailItem
                                          key={i}
                                          type={config.getType(item)}
                                          sessions={config.getSessions(item)}
                                          status={config.getStatus(item)}
                                          dateLabel={config.dateLabel}
                                          date={config.getDate(item)}
                                          last_treatment_date={config.getLastTreatmentDate(item)}
                                        />
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}

                      </tbody>
                    </table>
                  )}

                  {/* Empty State */}
                  {!modalLoading && modalData.length === 0 && (
                    <p className="text-center fw-bold">No Details found</p>
                  )}

                </div>
              </div>
            </div>
          </div>
        </>
      )}


    </>


  );
};

export default App;
